/**
 * 数据格式转换层
 * 后端 API 使用 snake_case，前端使用 camelCase
 */

// ===== 通用转换 =====

/**
 * 将 snake_case 对象转换为 camelCase
 */
export function snakeToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(snakeToCamel);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = snakeToCamel(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

/**
 * 将 camelCase 对象转换为 snake_case
 */
export function camelToSnake(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(camelToSnake);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((result, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      result[snakeKey] = camelToSnake(obj[key]);
      return result;
    }, {} as any);
  }
  return obj;
}

// ===== Event 转换 =====

export interface BackendEvent {
  id: number;
  created_at: string;
  updated_at: string;
  title: string;
  title_en: string;
  date: string;
  end_date: string;
  location: string;
  location_en: string;
  country: string;
  country_en: string;
  region: string;
  type: string;
  status: string;
  cover: string;
  description: string;
  description_en: string;
  tags: string[] | null;
  tags_en: string[] | null;
  month: number;
  max_capacity: number | null;
  registration_start: string;
  registration_deadline: string;
  gallery: string[] | null; // 活动照片数组
  // 价格设置
  is_paid_event: boolean;
  currency: string;
  early_bird_enabled: boolean;
  early_bird_price: number;
  early_bird_start_date: string;
  early_bird_end_date: string;
  regular_price: number;
  price_end_date: string;
  token_gating_enabled: boolean;
  token_symbol: string;
  token_contract_address: string;
  token_min_holding: number;
  token_holders_only: boolean;
}

export interface FrontendEvent {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  titleEn: string;
  date: string;
  endDate: string;
  location: string;
  locationEn: string;
  country: string;
  countryEn: string;
  region: 'asia' | 'europe' | 'americas' | 'africa' | 'oceania' | '';
  type: 'offline' | 'online' | 'hybrid' | '';
  status: 'upcoming' | 'past' | 'draft' | 'published' | 'cancelled';
  cover: string;
  description: string;
  descriptionEn: string;
  tags: string[];
  tagsEn: string[];
  month: number;
  maxCapacity?: number;
  registrationStart?: string;
  registrationDeadline?: string;
  gallery?: string[]; // 活动照片数组
  // 价格设置
  isPaidEvent?: boolean;
  currency?: string;
  earlyBirdEnabled?: boolean;
  earlyBirdPrice?: number;
  earlyBirdStartDate?: string;
  earlyBirdEndDate?: string;
  regularPrice?: number;
  priceEndDate?: string;
  tokenGatingEnabled?: boolean;
  tokenSymbol?: string;
  tokenContractAddress?: string;
  tokenMinHolding?: number;
  tokenHoldersOnly?: boolean;
}

export function transformEvent(backend: BackendEvent): FrontendEvent {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    title: backend.title || '',
    titleEn: backend.title_en || '',
    date: backend.date || '',
    endDate: backend.end_date || '',
    location: backend.location || '',
    locationEn: backend.location_en || '',
    country: backend.country || '',
    countryEn: backend.country_en || '',
    region: (backend.region || 'asia') as FrontendEvent['region'],
    type: (backend.type || 'offline') as FrontendEvent['type'],
    status: backend.status === 'published' ? 'upcoming' : 
            backend.status === 'draft' ? 'draft' : 
            backend.status === 'cancelled' ? 'cancelled' : 'past',
    cover: backend.cover || '',
    description: backend.description || '',
    descriptionEn: backend.description_en || '',
    tags: backend.tags || [],
    tagsEn: backend.tags_en || [],
    month: backend.month || 0,
    maxCapacity: backend.max_capacity || undefined,
    registrationStart: backend.registration_start || undefined,
    registrationDeadline: backend.registration_deadline || undefined,
    gallery: backend.gallery || [], // 活动照片
    // 价格设置
    isPaidEvent: backend.is_paid_event || false,
    currency: backend.currency || 'USD',
    earlyBirdEnabled: backend.early_bird_enabled || false,
    earlyBirdPrice: backend.early_bird_price || 0,
    earlyBirdStartDate: backend.early_bird_start_date || '',
    earlyBirdEndDate: backend.early_bird_end_date || '',
    regularPrice: backend.regular_price || 0,
    priceEndDate: backend.price_end_date || '',
    tokenGatingEnabled: backend.token_gating_enabled || false,
    tokenSymbol: backend.token_symbol || 'GTRA',
    tokenContractAddress: backend.token_contract_address || '',
    tokenMinHolding: backend.token_min_holding || 0,
    tokenHoldersOnly: backend.token_holders_only || false,
  };
}

export function transformEventToBackend(frontend: Partial<FrontendEvent>): any {
  return camelToSnake({
    title: frontend.title,
    titleEn: frontend.titleEn,
    date: frontend.date,
    endDate: frontend.endDate,
    location: frontend.location,
    locationEn: frontend.locationEn,
    country: frontend.country,
    countryEn: frontend.countryEn,
    region: frontend.region,
    type: frontend.type,
    status: frontend.status === 'upcoming' ? 'published' : frontend.status,
    cover: frontend.cover,
    description: frontend.description,
    descriptionEn: frontend.descriptionEn,
    tags: frontend.tags,
    tagsEn: frontend.tagsEn,
    month: frontend.month,
    maxCapacity: frontend.maxCapacity,
    registrationStart: frontend.registrationStart,
    registrationDeadline: frontend.registrationDeadline,
    gallery: frontend.gallery, // 活动照片
    // 价格设置
    isPaidEvent: frontend.isPaidEvent,
    currency: frontend.currency,
    earlyBirdEnabled: frontend.earlyBirdEnabled,
    earlyBirdPrice: frontend.earlyBirdPrice,
    earlyBirdStartDate: frontend.earlyBirdStartDate,
    earlyBirdEndDate: frontend.earlyBirdEndDate,
    regularPrice: frontend.regularPrice,
    priceEndDate: frontend.priceEndDate,
    tokenGatingEnabled: frontend.tokenGatingEnabled,
    tokenSymbol: frontend.tokenSymbol,
    tokenContractAddress: frontend.tokenContractAddress,
    tokenMinHolding: frontend.tokenMinHolding,
    tokenHoldersOnly: frontend.tokenHoldersOnly,
  });
}

// ===== Guest 转换 =====

export interface BackendGuest {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  name_en: string;
  title: string;
  title_en: string;
  institution: string;
  institution_en: string;
  country: string;
  country_en: string;
  photo: string;
  bio: string;
  bio_en: string;
  year: number;
  expertise: string[] | null;
  expertise_en: string[] | null;
  linkedin?: string;
  publications?: number;
  awards?: string[] | null;
  awards_en?: string[] | null;
}

export interface FrontendGuest {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  nameEn: string;
  title: string;
  titleEn: string;
  institution: string;
  institutionEn: string;
  country: string;
  countryEn: string;
  photo: string;
  bio: string;
  bioEn: string;
  year: number;
  expertise: string[];
  expertiseEn: string[];
  linkedin?: string;
  publications?: number;
  awards?: string[];
  awardsEn?: string[];
}

export function transformGuest(backend: BackendGuest): FrontendGuest {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    name: backend.name || '',
    nameEn: backend.name_en || '',
    title: backend.title || '',
    titleEn: backend.title_en || '',
    institution: backend.institution || '',
    institutionEn: backend.institution_en || '',
    country: backend.country || '',
    countryEn: backend.country_en || '',
    photo: backend.photo || '',
    bio: backend.bio || '',
    bioEn: backend.bio_en || '',
    year: backend.year || 0,
    expertise: backend.expertise || [],
    expertiseEn: backend.expertise_en || [],
    linkedin: backend.linkedin,
    publications: backend.publications,
    awards: backend.awards || [],
    awardsEn: backend.awards_en || [],
  };
}

export function transformGuestToBackend(frontend: Partial<FrontendGuest>): any {
  return camelToSnake({
    name: frontend.name,
    nameEn: frontend.nameEn,
    title: frontend.title,
    titleEn: frontend.titleEn,
    institution: frontend.institution,
    institutionEn: frontend.institutionEn,
    country: frontend.country,
    countryEn: frontend.countryEn,
    photo: frontend.photo,
    bio: frontend.bio,
    bioEn: frontend.bioEn,
    year: frontend.year,
    expertise: frontend.expertise,
    expertiseEn: frontend.expertiseEn,
    linkedin: frontend.linkedin,
    publications: frontend.publications,
    awards: frontend.awards,
    awardsEn: frontend.awardsEn,
  });
}

// ===== Donor 转换 =====

export interface BackendDonor {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  name_en: string;
  type: string;
  type_en: string;
  tier: string;
  abbr: string;
  color: string;
  year: number;
  description: string;
  description_en: string;
  logo: string;
}

export interface FrontendDonor {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  nameEn: string;
  type: 'organization' | 'individual' | 'partner' | 'donor';
  typeEn: string;
  tier: 'platinum' | 'gold' | 'silver' | 'bronze' | 'diamond' | 'honorary' | 'strategic';
  abbr?: string;
  color?: string;
  year: number;
  description: string;
  descriptionEn: string;
  logo?: string;
}

export function transformDonor(backend: BackendDonor): FrontendDonor {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    name: backend.name || '',
    nameEn: backend.name_en || '',
    type: (backend.type || 'organization') as FrontendDonor['type'],
    typeEn: backend.type_en || '',
    tier: (backend.tier || 'gold') as FrontendDonor['tier'],
    abbr: backend.abbr || undefined,
    color: backend.color || undefined,
    year: backend.year || 0,
    description: backend.description || '',
    descriptionEn: backend.description_en || '',
    logo: backend.logo || undefined,
  };
}

export function transformDonorToBackend(frontend: Partial<FrontendDonor>): any {
  return camelToSnake({
    name: frontend.name,
    nameEn: frontend.nameEn,
    type: frontend.type,
    typeEn: frontend.typeEn,
    tier: frontend.tier,
    abbr: frontend.abbr,
    color: frontend.color,
    year: frontend.year,
    description: frontend.description,
    descriptionEn: frontend.descriptionEn,
    logo: frontend.logo,
  });
}

// ===== Media 转换 =====

export interface BackendMedia {
  id: number;
  created_at: string;
  updated_at: string;
  source: string;
  source_logo_color: string;
  title: string;
  title_en: string;
  excerpt: string;
  excerpt_en: string;
  date: string;
  category: string;
  category_en: string;
  url: string;
  featured: boolean;
}

export interface FrontendMedia {
  id: string;
  createdAt: string;
  updatedAt: string;
  source: string;
  sourceLogoColor?: string;
  title: string;
  titleEn: string;
  excerpt: string;
  excerptEn: string;
  date: string;
  category?: string;
  categoryEn?: string;
  url: string;
  featured: boolean;
}

export function transformMedia(backend: BackendMedia): FrontendMedia {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    source: backend.source || '',
    sourceLogoColor: backend.source_logo_color || undefined,
    title: backend.title || '',
    titleEn: backend.title_en || '',
    excerpt: backend.excerpt || '',
    excerptEn: backend.excerpt_en || '',
    date: backend.date || '',
    category: backend.category || undefined,
    categoryEn: backend.category_en || undefined,
    url: backend.url || '',
    featured: backend.featured || false,
  };
}

export function transformMediaToBackend(frontend: Partial<FrontendMedia>): any {
  return camelToSnake({
    source: frontend.source,
    sourceLogoColor: frontend.sourceLogoColor,
    title: frontend.title,
    titleEn: frontend.titleEn,
    excerpt: frontend.excerpt,
    excerptEn: frontend.excerptEn,
    date: frontend.date,
    category: frontend.category,
    categoryEn: frontend.categoryEn,
    url: frontend.url,
    featured: frontend.featured,
  });
}

// ===== Staff 转换 =====

export interface BackendStaff {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  email: string;
  department: string;
  role_ids: string[];
  role_names: string[];
  permissions: string[];
}

export interface FrontendStaff {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  email: string;
  department?: string;
  roleIds: string[];
  roleNames: string[];
  permissions: string[];
}

export function transformStaff(backend: BackendStaff): FrontendStaff {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    name: backend.name || '',
    email: backend.email || '',
    department: backend.department || undefined,
    roleIds: backend.role_ids || [],
    roleNames: backend.role_names || [],
    permissions: backend.permissions || [],
  };
}

// ===== Team 转换 =====

export interface BackendTeam {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  name_en: string;
  title: string;
  title_en: string;
  institution: string;
  institution_en: string;
  photo: string;
  bio: string;
  bio_en: string;
  role: string;
  linkedin?: string;
}

export interface FrontendTeam {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  nameEn: string;
  title: string;
  titleEn: string;
  institution: string;
  institutionEn: string;
  photo: string;
  bio: string;
  bioEn: string;
  role: 'executive' | 'advisory';
  linkedin?: string;
}

export function transformTeam(backend: BackendTeam): FrontendTeam {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    name: backend.name || '',
    nameEn: backend.name_en || '',
    title: backend.title || '',
    titleEn: backend.title_en || '',
    institution: backend.institution || '',
    institutionEn: backend.institution_en || '',
    photo: backend.photo || '',
    bio: backend.bio || '',
    bioEn: backend.bio_en || '',
    role: (backend.role || 'executive') as FrontendTeam['role'],
    linkedin: backend.linkedin,
  };
}

// ===== Report 转换 =====

export interface BackendReport {
  id: number;
  created_at: string;
  updated_at: string;
  title_zh: string;
  title_en: string;
  desc_zh: string;
  desc_en: string;
  type: string;
  year: number;
  pages: number;
  cover: string;
  file_size: string;
  download_url: string;
}

export interface FrontendReport {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  type: 'report' | 'whitepaper' | 'brief';
  year: number;
  pages: number;
  cover: string;
  fileSize: string;
  downloadUrl: string;
}

export function transformReport(backend: BackendReport): FrontendReport {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    title: backend.title_zh || '',
    titleEn: backend.title_en || '',
    description: backend.desc_zh || '',
    descriptionEn: backend.desc_en || '',
    type: (backend.type || 'report') as FrontendReport['type'],
    year: backend.year || 0,
    pages: backend.pages || 0,
    cover: backend.cover || '',
    fileSize: backend.file_size || '',
    downloadUrl: backend.download_url || '',
  };
}

// ===== Milestone 转换 =====

export interface BackendMilestone {
  id: number;
  created_at: string;
  updated_at: string;
  year: string;
  title_zh: string;
  title_en: string;
  desc_zh: string;
  desc_en: string;
  icon: string;
  sort_order: number;
}

export interface FrontendMilestone {
  id: string;
  createdAt: string;
  updatedAt: string;
  year: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  icon: string;
  sortOrder: number;
}

export function transformMilestone(backend: BackendMilestone): FrontendMilestone {
  return {
    id: String(backend.id),
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    year: backend.year || '',
    title: backend.title_zh || '',
    titleEn: backend.title_en || '',
    description: backend.desc_zh || '',
    descriptionEn: backend.desc_en || '',
    icon: backend.icon || '',
    sortOrder: backend.sort_order || 0,
  };
}
